import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/SimpleAuthContext';
import { API_CONFIG } from '../config/apiConfig';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  UserIcon,
  AcademicCapIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';
import ProfileCompletionAlert from '../components/ProfileCompletionAlert';
import { checkProfileCompletion } from '../utils/profileValidation';
import { validateForCourseApplication } from '../utils/saIdValidation';
import './CourseApplication.css';

const CourseApplication = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, accessToken, makeAuthenticatedRequest } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [profileCheck, setProfileCheck] = useState(null);
  const [idValidation, setIdValidation] = useState(null);
  
  const [formData, setFormData] = useState({
    motivation_letter: '',
    additional_notes: '',
    intake_id: '',
    acknowledgment_accepted: false
  });

  // Fetch course details
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        console.log('🔄 Fetching course data for ID:', courseId);
        const apiUrl = API_CONFIG.courses.detail(courseId);
        console.log('🔄 API URL will be:', apiUrl);

        const response = await fetch(apiUrl);
        console.log('🔄 Response status:', response.status);
        console.log('🔄 Response headers:', Object.fromEntries(response.headers.entries()));

        if (response.ok) {
          const courseData = await response.json();
          console.log('✅ Course data loaded:', courseData.programme_name);
          setCourse(courseData);
        } else {
          const errorText = await response.text();
          console.error('❌ Course not found:', response.status, errorText);
          setError(`Course not found (Status: ${response.status})`);
        }
      } catch (error) {
        console.error('❌ Error fetching course:', error);
        setError('Failed to load course information');
      } finally {
        setLoading(false);
      }
    };

    if (courseId) {
      fetchCourse();
    } else {
      console.error('❌ No courseId provided in URL');
      setError('No course ID provided');
      setLoading(false);
    }
  }, [courseId]);

  // Redirect to login if not authenticated and check profile completion
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate(`/login?redirect=/apply/${courseId}`);
    } else if (user) {
      const profileCompletion = checkProfileCompletion(user);
      setProfileCheck(profileCompletion);

      // Check ID validation for course application
      const idCheck = validateForCourseApplication(user);
      setIdValidation(idCheck);
    }
  }, [isAuthenticated, loading, navigate, courseId, user]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    // Check ID validation first
    if (idValidation && !idValidation.canApply) {
      setError(idValidation.errors[0] || 'A valid South African ID number is required to apply for courses');
      return false;
    }

    if (!formData.motivation_letter.trim()) {
      setError('Motivation letter is required');
      return false;
    }

    if (formData.motivation_letter.trim().length < 100) {
      setError('Motivation letter must be at least 100 characters long');
      return false;
    }

    if (!formData.acknowledgment_accepted) {
      setError('You must acknowledge that you meet all prerequisites and requirements before submitting your application');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    if (!validateForm()) {
      setSubmitting(false);
      return;
    }

    console.log('🔄 Course Application: Starting submission process...');
    console.log('📋 Form Data:', formData);

    try {
      const hasAccessToken = Boolean(accessToken || localStorage.getItem('access_token'));
      if (!hasAccessToken) {
        setError('Your session has expired. Please sign in again before submitting your application.');
        return;
      }

      console.log('🔐 Course Application: Submitting with the authenticated request helper...');
      const response = await makeAuthenticatedRequest(API_CONFIG.registrations.register(courseId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const responseText = await response.text();
      let responseData = {};
      try {
        responseData = responseText ? JSON.parse(responseText) : {};
      } catch {
        console.error('❌ Application response was not valid JSON:', responseText);
      }

      if (!response.ok) {
        console.error('❌ Application error:', response.status, responseData);
        if (response.status === 401) {
          setError('Your session has expired. Please sign in again before submitting your application.');
        } else {
          setError(responseData.error || responseData.message || 'Application failed. Please try again.');
        }
        return;
      }

      console.log('✅ Application submitted successfully:', responseData);
      setSuccess(true);

      // Refresh course data and registration status
      if (window.refreshCourseData) {
        window.refreshCourseData();
      }

      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (error) {
      console.error('❌ Error submitting application:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="application-loading">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading course information...</p>
        </div>
      </div>
    );
  }

  if (error && !course) {
    return (
      <div className="application-error">
        <div className="error-content">
          <ExclamationTriangleIcon className="error-icon" />
          <h2 className="error-title">Error</h2>
          <p className="error-message">{error}</p>
          <Link to="/courses" className="error-back-link">
            <ArrowLeftIcon className="back-icon" />
            Back to Courses
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="application-success">
        <div className="success-content">
          <CheckCircleIcon className="success-icon" />
          <h2 className="success-title">Application Submitted!</h2>
          <p className="success-message">
            Your application for <strong>{course?.programme_name}</strong> has been successfully submitted.
          </p>
          <p className="success-redirect-text">
            You will be redirected to your dashboard in a few seconds...
          </p>
          <Link to="/dashboard" className="success-dashboard-link">
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="application-page">
      <div className="application-container">
        {/* Header */}
        <div className="application-header">
          <Link to={`/courses/${courseId}`} className="back-link">
            <ArrowLeftIcon className="back-icon" />
            Back to Course Details
          </Link>

          <div className="header-card">
            <div className="header-content">
              <div className="header-icon-container">
                <AcademicCapIcon className="header-icon" />
              </div>
              <div className="header-text">
                <h1 className="header-title">Apply for Course</h1>
                <h2 className="header-course-name">{course?.programme_name}</h2>
                <p className="header-details">
                  <span className="header-detail-item">Campus:</span> {course?.campus?.name} |
                  <span className="header-detail-item">Duration:</span> {course?.duration_months} months
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="application-grid">
          {/* Application Form */}
          <div className="form-section">
            <div className="form-header">
              <DocumentTextIcon className="form-header-icon" />
              <h3 className="form-header-title">Application Form</h3>
            </div>

            <div className="form-content">
              {/* Profile Completion Check */}
              {profileCheck && !profileCheck.canApplyForCourses && (
                <ProfileCompletionAlert user={user} />
              )}

              {/* ID Validation Check */}
              {idValidation && !idValidation.canApply && (
                <div className="id-validation-alert">
                  <div className="alert-header">
                    <IdentificationIcon className="alert-icon" />
                    <h4 className="alert-title">ID Number Required</h4>
                  </div>
                  <div className="alert-content">
                    <p className="alert-message">
                      {idValidation.errors[0] || 'A valid South African ID number is required to apply for courses.'}
                    </p>
                    <Link to="/profile" className="alert-action-link">
                      Complete Your Profile →
                    </Link>
                  </div>
                </div>
              )}

              {/* ID Validation Warnings */}
              {idValidation && idValidation.canApply && idValidation.warnings.length > 0 && (
                <div className="id-validation-warning">
                  <div className="warning-header">
                    <ExclamationTriangleIcon className="warning-icon" />
                    <h4 className="warning-title">Profile Recommendations</h4>
                  </div>
                  <div className="warning-content">
                    {idValidation.warnings.map((warning, index) => (
                      <p key={index} className="warning-message">{warning}</p>
                    ))}
                    <Link to="/profile" className="warning-action-link">
                      Update Profile →
                    </Link>
                  </div>
                </div>
              )}

              {error && (
                <div className="form-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="application-form">
                <div className="form-field">
                  <label htmlFor="motivation_letter" className="field-label">
                    Motivation Letter <span className="required-indicator">*</span>
                  </label>
                  <textarea
                    id="motivation_letter"
                    name="motivation_letter"
                    rows={6}
                    required
                    className="field-textarea"
                    placeholder="Please explain why you want to enroll in this course and how it aligns with your career goals..."
                    value={formData.motivation_letter}
                    onChange={handleInputChange}
                  />
                  <div className="field-help">
                    <p className="help-text">
                      Minimum 100 characters. Be specific about your goals and motivation.
                    </p>
                    <span className={`character-counter ${formData.motivation_letter.length >= 100 ? 'valid' : 'invalid'}`}>
                      {formData.motivation_letter.length}/100
                    </span>
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="additional_notes" className="field-label">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    id="additional_notes"
                    name="additional_notes"
                    rows={4}
                    className="field-textarea"
                    placeholder="Any additional information you'd like to share..."
                    value={formData.additional_notes}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Acknowledgment Checkbox */}
                <div className="form-field acknowledgment-field">
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      id="acknowledgment_accepted"
                      name="acknowledgment_accepted"
                      checked={formData.acknowledgment_accepted}
                      onChange={handleInputChange}
                      className="acknowledgment-checkbox"
                      required
                    />
                    <label htmlFor="acknowledgment_accepted" className="checkbox-label">
                      <span className="checkbox-text">
                        I acknowledge that I meet all the <strong>prerequisites and qualification requirements</strong> for this course as listed in the sidebar,
                        and I understand that I must bring <strong>original copies or certified copies</strong> of all relevant qualification documents
                        (certificates, transcripts, ID document) when I attend the registration appointment.
                        <strong className="warning-text"> Failure to provide required documentation may result in my application being declined.</strong>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <Link to={`/courses/${courseId}`} className="cancel-button">
                    <ArrowLeftIcon className="back-icon" />
                    Cancel
                  </Link>

                  <button
                    type="submit"
                    disabled={submitting || !formData.motivation_letter.trim() || formData.motivation_letter.length < 100 || !formData.acknowledgment_accepted || (profileCheck && !profileCheck.canApplyForCourses) || (idValidation && !idValidation.canApply)}
                    className="submit-button"
                  >
                    {submitting ? (
                      <>
                        <div className="loading-spinner-small"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="submit-icon" />
                        Submit Application
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="application-sidebar">
            {/* Applicant Info */}
            <div className="sidebar-card">
              <div className="sidebar-header">
                <UserIcon className="sidebar-header-icon" />
                <h3 className="sidebar-header-title">Applicant Information</h3>
              </div>

              <div className="sidebar-content">
                <div className="info-list">
                  <div className="info-item">
                    <span className="info-label">Name:</span>
                    <p className="info-value">{user?.first_name} {user?.last_name}</p>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <p className="info-value">{user?.email}</p>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Phone:</span>
                    <p className="info-value">{user?.phone_number || 'Not provided'}</p>
                  </div>
                </div>

                <div className="info-divider"></div>
                <Link to="/profile" className="profile-link">
                  Update Profile Information →
                </Link>
              </div>
            </div>

            {/* Course Requirements */}
            {course?.prerequisites && (
              <div className="sidebar-card prerequisites-card">
                <div className="sidebar-header">
                  <h3 className="sidebar-header-title">Prerequisites</h3>
                </div>
                <div className="sidebar-content">
                  <p className="prerequisites-text">{course.prerequisites}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseApplication;
